import { CursorPaginatedResponseSchema, DefaultCursorPageSchema, QueryBooleanSchema, } from 'notable-common';
import { z } from 'zod';
import { NotificationTimingSchema, NotificationTimingUpsertSchema } from './timing.js';
/**
 * A named sequence of send times. The schedule says *when*; a notification scheme pairs it with the
 * content to send at each of its timings.
 */
export const NotificationScheduleSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    name: z.string(),
    organizationId: z.uuid().nullable(),
    deletedTimestamp: z.iso.datetime().nullable(),
});
export const NotificationScheduleWithTimingsSchema = NotificationScheduleSchema.extend({
    timings: z.array(NotificationTimingSchema),
});
export const NotificationScheduleListEntrySchema = NotificationScheduleSchema.extend({
    messageCount: z.number().describe('How many timings the schedule has.'),
    relativeTime: z
        .string()
        .nullable()
        .describe("What the timings are anchored to: 'Before Start Time' (the appointment), 'After Intake Created' (the form set), 'After Intake Created, Before Start Time' when they mix, or null when no timing has a rule."),
});
export const NotificationScheduleCreateSchema = z
    .object({
    name: z.string().min(1),
    organizationId: z.uuid().optional(),
    timings: z.array(NotificationTimingUpsertSchema).min(1),
})
    .strict();
export const NotificationScheduleCloneSchema = z
    .object({
    newName: z.string().min(1),
})
    .strict();
/**
 * A patch. At least one field must be present, and `deletedTimestamp` accepts only null — the route
 * un-deletes a schedule but never back-dates a deletion. Sending `timings` replaces the whole set.
 */
export const NotificationScheduleUpdateSchema = z
    .object({
    name: z.string().min(1).optional(),
    organizationId: z.uuid().optional(),
    timings: z.array(NotificationTimingUpsertSchema).min(1).optional(),
    deletedTimestamp: z.null().optional(),
})
    .strict()
    .refine((values) => Object.keys(values).length > 0, {
    message: 'At least one field must be provided',
});
export const NotificationScheduleFindAllQuerySchema = z
    .object({
    q: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    includeDeleted: QueryBooleanSchema.optional(),
    organizationIds: z.array(z.uuid()).optional(),
})
    .strict();
export const NotificationScheduleListQuerySchema = z
    .object({
    q: z
        .string()
        .min(1)
        .optional()
        .describe('Return only schedules whose name contains this text.'),
    includeDeleted: QueryBooleanSchema.optional().describe('Include schedules that have been deleted.'),
    // An empty array would serialize to no parameter at all, and so ask for every organization.
    organizationIds: z
        .union([z.uuid(), z.array(z.uuid()).min(1)])
        .optional()
        .describe('Return only schedules owned by these organizations.'),
    page: DefaultCursorPageSchema,
})
    .strict();
export const NotificationScheduleListResponseSchema = CursorPaginatedResponseSchema(NotificationScheduleListEntrySchema);
//# sourceMappingURL=schedule.js.map